import { useMemo } from "react";

/**
 * Case-insensitive substring match for client-side list search (DAB-880).
 *
 * Every whitespace-separated token in the query must appear somewhere across
 * the given texts, so "иван софия" matches a row whose name is "Иван" and
 * whose city is "София" regardless of which field each token came from, and
 * regardless of order. Diacritic-insensitivity is not required.
 */
export function matchesQuery(texts, query) {
  const tokens = String(query ?? "")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  if (tokens.length === 0) return true;

  // Fields are joined with a space: tokens never contain whitespace, so no
  // token can match across the boundary between two fields.
  const haystack = texts
    .filter((text) => text !== null && text !== undefined)
    .map((text) => String(text).toLowerCase())
    .join(" ");

  return tokens.every((token) => haystack.includes(token));
}

/**
 * The rows of `items` that match `query`, for pages that search a list they
 * already hold in full. `fieldsFn(item)` returns the texts to search; keep it
 * a stable (module-level) function so the result stays memoised.
 */
export function useTextFilter(items, fieldsFn, query) {
  return useMemo(
    () => (items || []).filter((item) => matchesQuery(fieldsFn(item), query)),
    [items, fieldsFn, query],
  );
}
